import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import NotFound from '@/pages/NotFound';

const categoryLabels: Record<string, string> = {
  japan: '日本电影',
  korea: '韩国电影',
  anime: '动漫剧场版',
  theater: '剧场版合集',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movieId = parseInt(id || '0', 10);
  const movie = getMovieById(movieId);
  const relatedMovies = getRelatedMovies(movieId);

  if (!movie) {
    return <NotFound />;
  }

  const pageTitle = `${movie.title} - 日韩大片在线观看`;
  const pageDescription = `${movie.title}（${movie.titleEn}），${movie.year}年${movie.director}执导，主演${movie.cast.slice(0, 3).join('、')}。${movie.description}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    alternateName: movie.titleEn,
    description: movie.description,
    image: movie.coverImage,
    dateCreated: movie.releaseDate,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      reviewCount: Math.floor(movie.views / 10),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://rihan-dapian.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabels[movie.category] || '电影', item: `https://rihan-dapian.com/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  const formatViews = (views: number): string => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Banner */}
          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={movie.bannerImage}
              alt={movie.title}
              title={`${movie.title} - 日韩大片在线观看`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-900 via-background-900/60 to-transparent"></div>
          </section>

          {/* Article Content */}
          <article className="relative -mt-32 md:-mt-40">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              {/* Breadcrumb */}
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: categoryLabels[movie.category] || '电影', href: `/category/${movie.category}` },
                    { label: movie.title },
                  ]}
                />
              </div>

              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                {/* Cover Image */}
                <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
                  <div className="w-full aspect-[2/3] rounded-xl overflow-hidden">
                    <img
                      src={movie.coverImage}
                      alt={movie.title}
                      title={`${movie.title} - ${movie.year}年${movie.director}执导日韩大片`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Movie Info */}
                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
                    {movie.title}
                  </h1>
                  <p className="text-foreground-300 text-sm mb-4">{movie.titleEn}</p>

                  <div className="flex flex-wrap items-center gap-3 mb-5">
                    <span className="flex items-center gap-1 px-3 py-1 bg-accent-500/20 text-accent-400 rounded-full text-sm whitespace-nowrap">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill"></i>
                      </span>
                      <strong>{movie.rating}</strong> / 10
                    </span>
                    <span className="px-3 py-1 bg-background-700/50 text-foreground-300 rounded-full text-sm whitespace-nowrap">
                      {movie.year}
                    </span>
                    <span className="px-3 py-1 bg-background-700/50 text-foreground-300 rounded-full text-sm whitespace-nowrap">
                      {movie.duration}
                    </span>
                    <span className="text-sm text-foreground-400 whitespace-nowrap">
                      {formatViews(movie.views)} 次观看
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">导演：</span>
                      <span className="text-sm text-white font-medium">{movie.director}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">主演：</span>
                      <span className="text-sm text-white font-medium truncate">{movie.cast.join(' / ')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">类型：</span>
                      <span className="text-sm text-white font-medium">{categoryLabels[movie.category]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">上映：</span>
                      <span className="text-sm text-white font-medium">{movie.releaseDate}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {movie.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-secondary-700/50 text-secondary-300 rounded-full text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Full Description */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">剧情简介</h2>
              <div className="prose prose-sm max-w-none">
                <p className="text-foreground-700 leading-relaxed text-base">{movie.longDescription}</p>
              </div>
            </div>
          </section>

          {/* Related Movies */}
          {relatedMovies.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <div className="flex items-center justify-between mb-6 md:mb-8">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">相关推荐</h2>
                  <Link
                    to={`/category/${movie.category}`}
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
                  >
                    更多{movie.category === 'japan' ? '日本电影' : movie.category === 'korea' ? '韩国电影' : '电影'}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop>
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}